import { create } from 'zustand';
import type { ChatAgentType, ChatMessage, ConversationMeta } from '../types/agentChat';

interface AgentSession {
  messages: ChatMessage[];
  isThinking: boolean;
  sessionId: string;
  conversationId: string;
  error: string | null;
  processedMessageIds: Set<string>;
}

// Neither `customer` nor `concierge` reaches this store — Anna's console runs on useWsChat,
// not on this legacy Message Hub path. They are listed anyway so the objects `emptyByAgent`
// builds match the `Record<ChatAgentType, …>` the state declares: that Record is produced
// with an `as` cast, so a missing key is invisible to the type checker and shows up as an
// undefined session at runtime instead.
const AGENT_TYPES: ChatAgentType[] = [
  'marketing',
  'operation',
  'profit',
  'seo',
  'customer',
  'concierge',
];

interface AgentChatState {
  /**
   * The ACTIVE conversation per agent. Kept as the canonical per-agent view so
   * the chat hook, hub routing, and components can read `sessions[agentType]`
   * unchanged. Non-active conversations of the same agent live in `inactive`.
   */
  sessions: Record<ChatAgentType, AgentSession>;
  /** Stashed non-active conversations, keyed by agent then conversationId. */
  inactive: Record<ChatAgentType, Record<string, AgentSession>>;
  /** History sidebar rows per agent (newest first). */
  conversationLists: Record<ChatAgentType, ConversationMeta[]>;
  /** Whether we've already loaded history for this agent this session. */
  hydrated: Record<ChatAgentType, boolean>;

  addMessage: (agentType: ChatAgentType, message: ChatMessage) => void;
  setThinking: (agentType: ChatAgentType, thinking: boolean) => void;
  setError: (agentType: ChatAgentType, error: string | null) => void;
  resetChat: (agentType: ChatAgentType) => void;
  resetAll: () => void;

  // ── Multi-conversation actions ──
  setConversationList: (agentType: ChatAgentType, metas: ConversationMeta[]) => void;
  setHydrated: (agentType: ChatAgentType, value: boolean) => void;
  /** Replace the active session with one rebuilt from persisted history. */
  hydrateConversation: (
    agentType: ChatAgentType,
    conversationId: string,
    messages: ChatMessage[]
  ) => void;
  /** Stash the current active conversation and open a fresh empty one. */
  startNewConversation: (agentType: ChatAgentType) => string;
  /** Make `conversationId` active (restoring from `inactive` if present). */
  switchConversation: (agentType: ChatAgentType, conversationId: string) => void;
  /** Append a message to a specific conversation (active or stashed). */
  appendToConversation: (
    agentType: ChatAgentType,
    conversationId: string,
    message: ChatMessage
  ) => void;
  /** Set thinking on a specific conversation (active or stashed). */
  setThinkingForConversation: (
    agentType: ChatAgentType,
    conversationId: string,
    thinking: boolean
  ) => void;
}

function generateId(): string {
  return crypto.randomUUID();
}

export function createEmptySession(): AgentSession {
  // Backend websocket payloads return this value as `conversationId`,
  // but in our dashboard flow it is the same logical session identifier.
  // Keeping both fields equal lets us route incoming hub messages by id.
  const id = generateId();
  return {
    messages: [],
    isThinking: false,
    sessionId: id,
    conversationId: id,
    error: null,
    processedMessageIds: new Set(),
  };
}

function sessionFromHistory(conversationId: string, messages: ChatMessage[]): AgentSession {
  return {
    messages,
    isThinking: false,
    sessionId: conversationId,
    conversationId,
    error: null,
    processedMessageIds: new Set(messages.map((m) => m.id)),
  };
}

function appendMessage(session: AgentSession, message: ChatMessage): AgentSession {
  if (session.processedMessageIds.has(message.id)) return session;
  return {
    ...session,
    messages: [...session.messages, message],
    processedMessageIds: new Set([...session.processedMessageIds, message.id]),
  };
}

function emptyByAgent<T>(factory: () => T): Record<ChatAgentType, T> {
  return AGENT_TYPES.reduce(
    (acc, type) => {
      acc[type] = factory();
      return acc;
    },
    {} as Record<ChatAgentType, T>
  );
}

export const useAgentChatStore = create<AgentChatState>()((set, get) => ({
  sessions: emptyByAgent(createEmptySession),
  inactive: emptyByAgent(() => ({}) as Record<string, AgentSession>),
  conversationLists: emptyByAgent(() => [] as ConversationMeta[]),
  hydrated: emptyByAgent(() => false),

  addMessage: (agentType, message) => {
    const { sessions } = get();
    set({
      sessions: { ...sessions, [agentType]: appendMessage(sessions[agentType], message) },
    });
  },

  setThinking: (agentType, thinking) => {
    const { sessions } = get();
    set({
      sessions: {
        ...sessions,
        [agentType]: { ...sessions[agentType], isThinking: thinking },
      },
    });
  },

  setError: (agentType, error) => {
    const { sessions } = get();
    set({
      sessions: {
        ...sessions,
        [agentType]: { ...sessions[agentType], error },
      },
    });
  },

  resetChat: (agentType) => {
    const { sessions } = get();
    set({
      sessions: { ...sessions, [agentType]: createEmptySession() },
    });
  },

  resetAll: () => {
    set({
      sessions: emptyByAgent(createEmptySession),
      inactive: emptyByAgent(() => ({}) as Record<string, AgentSession>),
      conversationLists: emptyByAgent(() => [] as ConversationMeta[]),
      hydrated: emptyByAgent(() => false),
    });
  },

  setConversationList: (agentType, metas) => {
    const { conversationLists } = get();
    set({ conversationLists: { ...conversationLists, [agentType]: metas } });
  },

  setHydrated: (agentType, value) => {
    const { hydrated } = get();
    set({ hydrated: { ...hydrated, [agentType]: value } });
  },

  hydrateConversation: (agentType, conversationId, messages) => {
    const { sessions } = get();
    set({
      sessions: {
        ...sessions,
        [agentType]: sessionFromHistory(conversationId, messages),
      },
    });
  },

  startNewConversation: (agentType) => {
    const { sessions, inactive } = get();
    const current = sessions[agentType];
    const fresh = createEmptySession();
    // Stash the outgoing conversation only if it has content worth keeping.
    const nextInactive =
      current.messages.length > 0
        ? { ...inactive[agentType], [current.conversationId]: current }
        : inactive[agentType];
    set({
      sessions: { ...sessions, [agentType]: fresh },
      inactive: { ...inactive, [agentType]: nextInactive },
    });
    return fresh.conversationId;
  },

  switchConversation: (agentType, conversationId) => {
    const { sessions, inactive } = get();
    const current = sessions[agentType];
    if (current.conversationId === conversationId) return;

    const stash = { ...inactive[agentType] };
    if (current.messages.length > 0) {
      stash[current.conversationId] = current;
    }
    // Restore the target if we already have it; otherwise open an empty shell
    // bound to that id (the caller will hydrate it from the backend).
    const restored = stash[conversationId];
    if (restored) delete stash[conversationId];
    const next: AgentSession =
      restored ?? {
        messages: [],
        isThinking: false,
        sessionId: conversationId,
        conversationId,
        error: null,
        processedMessageIds: new Set(),
      };
    set({
      sessions: { ...sessions, [agentType]: next },
      inactive: { ...inactive, [agentType]: stash },
    });
  },

  appendToConversation: (agentType, conversationId, message) => {
    const { sessions, inactive } = get();
    if (sessions[agentType].conversationId === conversationId) {
      set({
        sessions: { ...sessions, [agentType]: appendMessage(sessions[agentType], message) },
      });
      return;
    }
    const stashed = inactive[agentType][conversationId];
    if (stashed) {
      set({
        inactive: {
          ...inactive,
          [agentType]: {
            ...inactive[agentType],
            [conversationId]: appendMessage(stashed, message),
          },
        },
      });
    }
  },

  setThinkingForConversation: (agentType, conversationId, thinking) => {
    const { sessions, inactive } = get();
    if (sessions[agentType].conversationId === conversationId) {
      set({
        sessions: {
          ...sessions,
          [agentType]: { ...sessions[agentType], isThinking: thinking },
        },
      });
      return;
    }
    const stashed = inactive[agentType][conversationId];
    if (stashed) {
      set({
        inactive: {
          ...inactive,
          [agentType]: {
            ...inactive[agentType],
            [conversationId]: { ...stashed, isThinking: thinking },
          },
        },
      });
    }
  },
}));

/**
 * Resolve which (agentType, conversationId) owns a given hub conversationId,
 * searching both active and stashed conversations. Used by the chat hook to
 * route an incoming Message Hub event to the correct conversation even after
 * the user has switched agents or conversations.
 */
export function resolveConversationBySessionId(
  sessionId: string
): { agentType: ChatAgentType; conversationId: string } | null {
  const { sessions, inactive } = useAgentChatStore.getState();
  for (const agentType of AGENT_TYPES) {
    if (sessions[agentType].sessionId === sessionId) {
      return { agentType, conversationId: sessions[agentType].conversationId };
    }
    if (inactive[agentType][sessionId]) {
      return { agentType, conversationId: sessionId };
    }
  }
  return null;
}
